import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo } from 'react'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import AppsSection from '../../../../reusableFormFields/AppsSection'
import UrlsSection from '../../../../reusableFormFields/UrlsSection'
import RadioBlock from '../../../../../../components/Form/RadioBlock'
import ChannelsSection from '../../../../reusableFormFields/ChannelsSection'

import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { useGetAttachedChannels } from './hooks'

import {
  dv360BulkTargetingWasUpdatedSelector,
  dv360ChannelsWasLoadedSelector,
  dv360TargetingSelector,
  updateDv360BulkTargetingErrorSelector,
  updateDv360BulkTargetingIsLoadingSelector
} from '../../../../../../modules/selectors/dv360Constants'
import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import {
  clearUpdateDv360BulkTargeting,
  updateDv360BulkTargeting
} from '../../../../../../modules/actions/dv360Constants'

import { targetingTypes } from '../../../LineItemDv360Create/fields'
import {
  APPS,
  CHANNELS,
  CHANNELS_EXCLUDED,
  TARGET_SPECIFIC_URLS_OR_APPS,
  URLS,
  YES_USING_PRE_SAVED_SITE_LISTS
} from '../../../../UploadDV360AdCreative/UploadDV360AdCreativeForm/LineItemForm/fields'
import { HISTORY_META_EDIT_SECTION, TYPE_LINE_ITEM } from '../../../../../../constants/changeHistory'
import { OPTION_YES } from '../../../../../../constants/forms'
import { getInitialValues } from './fields'

function UrlsAndAppsSectionForm({ editItemData: item, title, ...formProps }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const targeting = useSelector(dv360TargetingSelector)
  const dv360BulkTargetingWasUpdated = useSelector(dv360BulkTargetingWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)
  const dv360ChannelsWasLoaded = useSelector(dv360ChannelsWasLoadedSelector)

  const { attachedChannels, excludedAttachedChannels } = useGetAttachedChannels()

  const initialValues = useMemo(() => {
    return getInitialValues({ targeting, attachedChannels, excludedAttachedChannels })
  }, [targeting, attachedChannels, excludedAttachedChannels])

  const handleSubmit = useCallback(
    values => {
      const filledUrls = values[URLS].filter(urlTableRow => urlTableRow?.[0]?.value).map(
        urlTableRow => urlTableRow?.[0]?.value
      )
      const filledExcludedUrls = values[URLS].filter(urlTableRow => urlTableRow?.[1]?.value).map(
        urlTableRow => urlTableRow?.[1]?.value
      )

      const filledApps = values[APPS].filter(appTableRos => appTableRos?.[0]?.value).map(
        appTableRos => appTableRos?.[0]?.value
      )
      const filledExcludedApps = values[APPS].filter(appTableRos => appTableRos?.[1]?.value).map(
        appTableRos => appTableRos?.[1]?.value
      )

      const channels = values[CHANNELS]
      const excludedChannels = values[CHANNELS_EXCLUDED]

      const urlsTargeting = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_URL,
          assigned_targeting_options:
            values[TARGET_SPECIFIC_URLS_OR_APPS] === OPTION_YES
              ? [...filledUrls.map(url => ({ url })), ...filledExcludedUrls.map(url => ({ url, negative: true }))]
              : // remove options if another radio button is selected
                []
        }
      ]

      const appsTargeting = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_APP,
          assigned_targeting_options:
            values[TARGET_SPECIFIC_URLS_OR_APPS] === OPTION_YES
              ? [
                  ...filledApps.map(app => ({ app_id: app })),
                  ...filledExcludedApps.map(app => ({ app_id: app, negative: true }))
                ]
              : // remove options if another radio button is selected
                []
        }
      ]

      const channelsTargeting = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_CHANNEL,
          assigned_targeting_options:
            values[TARGET_SPECIFIC_URLS_OR_APPS] === YES_USING_PRE_SAVED_SITE_LISTS
              ? [
                  ...channels.map(channel => ({
                    channel_id: channel.value
                  })),
                  ...excludedChannels.map(channel => ({
                    channel_id: channel.value,
                    negative: true
                  }))
                ]
              : // remove options if another radio button is selected
                []
        }
      ]

      const updateData = {
        account: selectedAdAccountId,
        line_item_id: item.id,
        requests: [...urlsTargeting, ...appsTargeting, ...channelsTargeting],
        meta: {
          [HISTORY_META_EDIT_SECTION]: title,
          override: {
            item_id: item.id,
            item_type: TYPE_LINE_ITEM,
            action: 'update'
          }
        }
      }

      dispatch(updateDv360BulkTargeting(updateData, true))
    },
    [dispatch, item.id, selectedAdAccountId, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { values, setFieldValue } = formik

  const handleClearUpdateItem = useCallback(() => {
    dispatch(clearUpdateDv360BulkTargeting())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={manualSuccessSubmit || dv360BulkTargetingWasUpdated}
      clearEditItem={handleClearUpdateItem}
      errorSelector={updateDv360BulkTargetingErrorSelector}
      isLoadingSelector={updateDv360BulkTargetingIsLoadingSelector}
      {...formProps}
    >
      <br />
      <RadioBlock
        setFieldValue={setFieldValue}
        id={TARGET_SPECIFIC_URLS_OR_APPS + YES_USING_PRE_SAVED_SITE_LISTS}
        name={TARGET_SPECIFIC_URLS_OR_APPS}
        value={YES_USING_PRE_SAVED_SITE_LISTS}
        selectedValue={values[TARGET_SPECIFIC_URLS_OR_APPS]}
        label={t('Pre-saved site lists')}
      >
        {!dv360ChannelsWasLoaded ? <Skeleton count={4} /> : <ChannelsSection formik={formik} />}
      </RadioBlock>
      <RadioBlock
        setFieldValue={setFieldValue}
        id={TARGET_SPECIFIC_URLS_OR_APPS + OPTION_YES}
        name={TARGET_SPECIFIC_URLS_OR_APPS}
        value={OPTION_YES}
        selectedValue={values[TARGET_SPECIFIC_URLS_OR_APPS]}
        label={t('Manually entering the sites and apps')}
      >
        <UrlsSection formik={formik} />
        <AppsSection formik={formik} />
      </RadioBlock>
    </EditForm>
  )
}

export default UrlsAndAppsSectionForm
